import {
  SKY_AUTHORED_BOSSES,
  SKY_AUTHORED_PROPS,
  SKY_AUTHORED_ENEMIES,
  skyAuthoredBossSpec,
} from './220-sky-authored-bosses.mjs';
import { SKY_STAGES, SKY_FINAL_GUARDIANS } from '../../../packages/domain/src/76-sky-campaign.mjs';

/** Authored public filenames. This is the active asset inventory; historical
 * exports remain source evidence and must not enter the opening download. */
export const SKY_MODEL_FILES = Object.freeze({
  rider: '36-pilot',
  leviathan: '11-leviathan',
  sentinel: '12-sentinel',
  oracle: '33-oracle',
  titan: '13-titan',
  drone: '14-drone',
  manta: '15-manta',
  arch: '16-arch',
  obelisk: '17-obelisk',
  tree: '18-tree',
  mushroom: '19-mushroom',
  mask: '20-mask',
  nautilus: '21-nautilus',
  carrier: '22-carrier',
  twin: '23-twin',
  skeletal: '24-skeletal',
  rock: '25-rock',
  gunship: '26-gunship',
  ace: '27-ace',
  mammoth: '34-mammoth',
  mechwalker: '35-walker',
});
/** Encounter-specific assets load through stage readiness. The opening stage
 * uses its own guardian; later encounter stages require their accepted assembly.
 * Missing artwork fails readiness instead of falling back to a legacy guardian.
 * Campaign selection remains domain76-owned. */
export const SKY_OPTIONAL_MODEL_FILES = Object.freeze({
  gildedLeviathan: '37-gilded-whale',
  cathedral: 'authored-bosses/03-cathedral',
  ...Object.fromEntries(Object.entries(SKY_AUTHORED_ENEMIES).map(([id, spec]) => [id, spec.file])),
  ...Object.fromEntries(Object.entries(SKY_AUTHORED_PROPS).map(([id, spec]) => [id, spec.file])),
  ...Object.fromEntries(
    Object.entries(SKY_AUTHORED_BOSSES).map(([id, spec]) => [id, `authored-bosses/${spec.file}`]),
  ),
  dolphin: '38-dolphin',
  refineryTower: '41-refinery-tower',
  refineryTowerLod: '42-refinery-tower-mobile',
});
/** Existing world batches and ambient transports are built eagerly. Their
 * dependencies, pilot and escort geometry must exist before the first draw. */
export const SKY_OPENING_ASSETS = Object.freeze([
  'rider',
  'arch',
  'obelisk',
  'tree',
  'mushroom',
  'rock',
  'drone',
  'manta',
  'carrier',
  'sentinel',
]);
export function skyBossAssetKey(item) {
  if (item.artVariant === 'cathedral-of-thunder') return 'cathedral';
  if (skyAuthoredBossSpec(item.artVariant)) return item.artVariant;
  if (item.artVariant === 'gilded-leviathan') return 'gildedLeviathan';
  const profile = item.profile ?? '';
  return item.artVariant === 'first-dragon'
    ? 'prototypeDragon'
    : item.design === 1
      ? 'oracle'
      : profile === 'carrier'
        ? 'carrier'
        : ['swarm', 'shellSwarm'].includes(profile)
          ? 'nautilus'
          : profile === 'stoneOracle'
            ? 'mask'
            : profile === 'twin'
              ? 'twin'
              : ['skeletalTwin', 'skeletalSerpent'].includes(profile)
                ? 'skeletal'
                : (item.form ?? 'leviathan');
}
/** Asset identity follows the campaign before spawn, or an explicitly selected
 * encounter bound to this sector. A prior sector's live scene cannot leak into
 * next-stage prefetch or make a different variant appear ready. */
export function skyStageArtVariant(state) {
  const stage = SKY_STAGES[state.sector];
  if (!stage) return null;
  if (stage.encounterId === 'cathedral-of-thunder') return 'cathedral-of-thunder';
  if (skyAuthoredBossSpec(stage.artVariant)) return stage.artVariant;
  return stage.encounterId === 'gilded-leviathan' ||
    state.leviathan?.sector === state.sector ||
    (state.boss?.artVariant === 'gilded-leviathan' && state.boss.encounterSector === state.sector)
    ? 'gilded-leviathan'
    : null;
}
/** A phase is playable only after every possible craft/bonus and its guardian
 * family is ready. Rush stages include every authored guardian, not only the
 * current encounter. Unknown sectors fail instead of silently omitting assets. */
export function skyStageAssets(state, prototype = false) {
  const stage = SKY_STAGES[state.sector];
  if (!stage) throw new RangeError('Unknown Sky Rider stage');
  const encounters =
    stage.kind === 'rush' ? SKY_FINAL_GUARDIANS : stage.kind === 'bonus' ? [] : [stage];
  const gilded = skyStageArtVariant(state) === 'gilded-leviathan';
  const bosses = gilded
    ? [{ ...state.boss, kind: 'boss', artVariant: 'gilded-leviathan' }]
    : encounters.map((encounter) => ({
        kind: 'boss',
        form: encounter.bossForm,
        profile: encounter.bossProfile,
        design: encounter.bossDesign,
        ...(encounter.encounterId === 'cathedral-of-thunder' ||
        skyAuthoredBossSpec(encounter.artVariant)
          ? {
              artVariant:
                encounter.encounterId === 'cathedral-of-thunder'
                  ? 'cathedral-of-thunder'
                  : encounter.artVariant,
            }
          : {}),
        ...(prototype && encounter.artVariant === 'first-dragon'
          ? { artVariant: 'first-dragon' }
          : {}),
      }));
  const bonuses = gilded ? [] : ['mammoth', 'mechwalker'];
  const keys = new Set([...SKY_OPENING_ASSETS, 'gunship', 'ace', ...bonuses]);
  for (const boss of bosses) {
    keys.add(skyBossAssetKey(boss));
    if (boss.profile === 'solarTitan') keys.add('leviathan');
  }
  if (gilded) for (const key of ['dolphin', 'refineryTower', 'refineryTowerLod']) keys.add(key);
  const props = (
    stage.worldId === 'twin-executioners' && stage.authoredBoss
      ? ['executioner-crimson', 'twin-gantry']
      : stage.worldId === 'scrap-emperor'
        ? ['scrap-supplies']
        : stage.worldId === 'cinderheart'
          ? ['cinderheart-coolant', 'cinderheart-slag']
          : stage.worldId === 'prism-widow'
            ? ['prism-mirrors']
            : stage.worldId === 'mourning-bride'
              ? ['mourning-bride-wreck', 'mourning-bride-echo']
              : stage.worldId === 'iron-orchid'
                ? ['iron-orchid-irrigation']
                : stage.worldId === 'vesper'
                  ? ['vesper-crystal']
                  : []
  ).map((artVariant) => ({ kind: 'authored-prop', artVariant }));
  for (const prop of props) keys.add(prop.artVariant);
  if (stage.enemyArtVariant) keys.add(stage.enemyArtVariant);
  return {
    keys: [...keys],
    bosses,
    warmItems: [
      ...bosses,
      ...props,
      ...['scout', 'bulwark', 'ace', 'flanker'].map((enemyClass) => ({
        kind: 'drone',
        enemyClass,
        ...(stage.enemyArtVariant ? { artVariant: stage.enemyArtVariant } : {}),
      })),
      ...bonuses.map((bonusForm) => ({ kind: 'bonus', bonusForm })),
    ],
  };
}
